/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useRef, useState } from 'react';
import Layout from 'antd/lib/layout/layout';
import { useHistory } from 'react-router-dom';
import {
  Pagination, Select, Table, Modal,
} from 'antd';

import { useFormik } from 'formik';
import classNames from 'classnames';
import isEqual from 'lodash.isequal';
import useAssetsData from './useAssetsData';
import './asset-types.less';
import PageTitle from '../../lib/page-title';
import { IAssetTypes } from '../../../store/modules/asset/i-asset';
import SecondaryButton from '../../lib/secondary-button';
import PrimaryButton from '../../lib/primary-button';
import ImportAssetTypesModal from './import';
import { useActions } from '../../providers/actions-provider/actions.context';
import capitalize from '../../../utils/capitalize';
import checkPermission from '../../../utils/permissions/permissions';
import PermissionProvider from '../../providers/permissionProvider';
import { AssetIcon } from '../../lib/asset-icon/AssetIcon';
import { useAssetIcon } from '../../hooks/useAssetIcon';
import { PROJECTION_CATCHUP_MS } from '../../../store/modules/asset/asset.actions';

const AssetTypeIcon = (
  { code, onPreview }: { code?: string; onPreview: () => void },
): JSX.Element => {
  const { url } = useAssetIcon(code);
  return (
    <button
      type="button"
      onClick={onPreview}
      title="View icon"
      style={{
        background: 'none', border: 'none', padding: 0, cursor: 'pointer', lineHeight: 0,
      }}
    >
      <AssetIcon src={url} size={28} />
    </button>
  );
};

const IconPreviewModal = (
  { code, onClose }: { code: string; onClose: () => void },
): JSX.Element => {
  const { url } = useAssetIcon(code);
  return (
    <Modal
      visible
      title={code}
      onCancel={onClose}
      onOk={onClose}
      okText="Close"
      cancelButtonProps={{ style: { display: 'none' } }}
      centered
      width={320}
    >
      <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
        <AssetIcon src={url} size={240} />
      </div>
    </Modal>
  );
};

export interface IFilter {
  field: string,
  type: string,
  value: string
}

interface IAssetFilter {
  header: string;
  field: string;
}

const AssetTypes = (): JSX.Element => {
  const {
    onClick, onSearch, data, initialFilter, onClearFilters,
  } = useAssetsData();
  const { assetActions } = useActions();
  const { push } = useHistory();
  const [currentFilter, setCurrentFilter] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const { Option } = Select;
  const pageSizeOptions = ['10', '12', '15', '20', '50', '100'];
  const [pageSize, setPageSize] = useState(10);
  const [skip, setSkip] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [finalFilter, setFinalFilter] = useState('');
  const [previewCode, setPreviewCode] = useState<string | null>(null);
  const [importVisible, setImportVisible] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | undefined>();

  useEffect(() => () => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
    }
  }, []);
  const hasNotWritePermission = !checkPermission('assetTypes', 'write');
  const finalColumns = [{
    title: '',
    key: 'icon',
    dataIndex: 'icon',
    align: 'center' as const,
    width: 64,
    // eslint-disable-next-line react/display-name
    render: (text: string, record: IAssetTypes) => (
      <AssetTypeIcon code={record.code} onPreview={() => setPreviewCode(record.code ?? null)} />
    ),
  }, ...data.columns, {
    title: '',
    key: 'details',
    dataIndex: 'details',
    // eslint-disable-next-line react/display-name
    render: (text: string, record: IAssetTypes) => (
      <SecondaryButton
        type="button"
        className="button-edit ant-btn--small"
        onClick={() => {
          onClick(record.id as number);
        }}
      >
        {!hasNotWritePermission ? 'Edit' : 'Details'}
      </SecondaryButton>
    ),
  }];

  const [filterModalVisible, setFilterModalVisible] = useState(false);

  const formikForSorting = useFormik({
    initialValues: {
      sort: '',
      defaultSortValue: '',
    },
    enableReinitialize: true,
    onSubmit: async () => null,
  });
  const {
    setFieldValue: setSortValue,
    values: { sort: sortValue, defaultSortValue: initialSortValue },
  } = formikForSorting;
  const sortData = data.fields.map((el, i) => [{ [`{ "${el}" : 1 }`]: `${capitalize(data.headers[i])}: ASC` }, { [`{ "${el}" : -1 }`]: `${capitalize(data.headers[i])}: DESC` }]).flat(1);

  const formikForFilter = useFormik({
    initialValues: {
      filters: [{
        field: '',
        type: '',
        value: '',
      }],
    },
    onSubmit: async (values) => {
      if (isEqual(initialFilter, values.filters[0])) {
        setFilterModalVisible(false);
        await assetActions.getAssetTypes(
          pageSize, pageNumber, sortValue,
        );
      } else {
        setFilterModalVisible(false);
        const filtersQuery = JSON.stringify(values.filters.reduce(
          (acc, filter) => Object.assign(
            acc,
            { [filter.field]: { [filter.type]: filter.value } },
          ), {},
        ));
        await assetActions.getAssetTypes(
          pageSize, pageNumber, sortValue, filtersQuery,
        );
        setFinalFilter(filtersQuery);
      }
    },
    enableReinitialize: true,
  });

  const {
    setFieldValue,
    values: { filters },
    submitForm,
    resetForm,
  } = formikForFilter;
  return (
    <Layout className="main-container">
      <PermissionProvider pageId="assetTypes">
        {previewCode !== null && (
          <IconPreviewModal code={previewCode} onClose={() => setPreviewCode(null)} />
        )}
        {importVisible && (
          <ImportAssetTypesModal
            onClose={() => setImportVisible(false)}
            onImported={() => {
              const refresh = (): void => {
                assetActions.getAssetTypes(pageSize, skip, sortValue, finalFilter);
              };
              refresh();
              refreshTimer.current = setTimeout(refresh, PROJECTION_CATCHUP_MS);
            }}
          />
        )}
        <PageTitle title="Asset Types">
          <PrimaryButton
            type="button"
            title="Import Asset Types"
            onClick={() => setImportVisible(true)}
            disabled={hasNotWritePermission}
          />
          {/* <PrimaryButton
            title="New Asset Type"
            onClick={() => push('asset-data/new')}
            disabled={hasNotWritePermission}
          /> */}
        </PageTitle>

        <div className="table__wrapper">
          {/*
          <div
            style={{ paddingBottom: 0 }}
            className="filter__wrapper execute-report__filter--wrapper">
            <div className="filter__range">
              <div className="report-execution-filter__wrapper">
                <FormikProvider value={formikForSorting}>
                  <Form>
                    <div className="filter__form select">
                      <p className="filter__form title">Sort</p>
                      <Select
                        size="large"
                        className="ant__select"
                        value={sortValue}
                        defaultValue={sortValue}
                        labelInValue={false}
                        onChange={(value) => {
                          setSortValue('defaultSortValue', '');
                          if (value === '') {
                            setSortValue('sort', '');
                          } else {
                            setSortValue('sort', value);
                          }
                        }}
                      >
                        {sortData && sortData.length && sortData.map((item) => {
                          const selector = Object.entries(item).flat(1);
                          const uuid = v4();
                          return (
                            <Option
                              value={selector[0]}
                              key={uuid}
                            >
                              {selector[1]}
                            </Option>
                          );
                        })}
                        <Option
                          value=""
                          key="uniqueNoneForSortString"
                        >
                          none
                        </Option>
                      </Select>
                    </div>
                  </Form>
                </FormikProvider>
              </div>
              <button
                type="button"
                className="filter__btn" onClick={() => setFilterModalVisible(!filterModalVisible)}>
                <FilterIcon />
              </button>
              <ReloadOutlined
                className="filter__btn"
                onClick={() => {
                  assetActions.getAssetTypes(pageSize, skip, sortValue, finalFilter);
                }}
              />
            </div>
            {filterModalVisible && (
              <FormikProvider value={formikForFilter}>
                <FilterModal
                  filters={filters}
                  fields={data.assetsFilters}
                  setFieldValue={setFieldValue}
                  initialFilter={initialFilter}
                  onSubmit={submitForm}
                  setVisible={setFilterModalVisible}
                  onClear={() => onClearFilters()}
                  resetForm={resetForm}
                  wrapperClassName="report-execution-filter__modal"
                />
              </FormikProvider>
            )}

          </div>
          */}
          <div
            className="column-items-size"
            style={{
              marginTop: 0,
              borderLeft: '1px solid #f1f2f5',
              borderRight: '1px solid #f1f2f5',
            }}
          >
            <p className="column-items-size__text">{`Showing records ${skip + 1} through ${skip + pageSize} of ${data.count || 'waiting'}`}</p>
            <div className="column-items-size__page-size">
              <span className="column-items-size__text">Page size</span>
              <Select
                defaultValue={pageSize}
                onChange={async (size) => {
                  await assetActions.getAssetTypes(+size, skip, sortValue, finalFilter);
                  setPageSize(+size);
                }}
              >
                {
                  pageSizeOptions.map((option, i) => (
                    <Option
                      value={option}
                      key={`${option + i}`}
                    >
                      {option}
                    </Option>
                  ))
                }

              </Select>
            </div>
          </div>
          <Table
            size="large"
            className="table"
            columns={finalColumns.filter((el) => el.key !== 'id' && el.key !== 'tenant' && el.key !== 'Id')}
            dataSource={data.rows}
            bordered={false}
            // rowKey={(record, i) => `${record.id} ${i}`}
            loading={data.loading}
            // position: ['bottomCenter'],
            pagination={false}
          />
          <Pagination
            className={classNames('custom-pagination-assets', {
              none: !data.rows?.length,
            })}
            defaultCurrent={1}
            showSizeChanger={false}
            pageSize={pageSize}
            // hideOnSinglePage
            total={data.count}
            // defaultPageSize={document.documentElement.clientHeight <= 800 ? 12 : 15}
            onChange={(page) => {
              if (pageSize) {
                setPageNumber(page);
                const skipOnPagination = (page - 1) * pageSize;
                setSkip(skipOnPagination);
                assetActions.getAssetTypes(pageSize, skipOnPagination, sortValue, finalFilter);
              }
            }}
          />
        </div>
      </PermissionProvider>
    </Layout>
  );
};

AssetTypes.displayName = 'AssetTypes';

export default AssetTypes;
